/**
 * « Mon avatar animé » (profil admin, migration 0675) : les 8 images de
 * l'avatar qui parle dans les animations SKILLCARD quand c'est TA voix
 * enregistrée qui joue. Image 1 = bouche fermée (aussi au générique), 2 à 8 =
 * bouche ouverte. Chaque image se remplace ; l'aperçu fait « parler »
 * l'avatar ; « Enregistrer » dépose les nouvelles images puis la ligne.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Alert, AlertDescription } from '@jeraisonne/ui';
import { SpeakerAvatar } from '@jeraisonne/ui/components/animations/speaker-avatar';
import { MOUTH_FRAME_MS, mouthFrameAt, SPEAKER_FRAME_COUNT } from '@jeraisonne/animation';
import {
  spriteFileIssue,
  spriteUrl,
  SPRITE_MIME_TYPES,
  useMySpeakerSprites,
  useSaveMySpeakerSprites,
  type SpriteSlot,
} from '@/lib/animations/speakers';

const EMPTY: SpriteSlot[] = Array.from({ length: SPEAKER_FRAME_COUNT }, () => ({
  path: null,
  file: null,
}));

/** Libellé d'un emplacement. */
export function spriteSlotLabel(i: number): string {
  return i === 0 ? '1 · bouche fermée' : `${i + 1} · bouche ouverte`;
}

export function SpeakerSpritesEditor({
  userId,
  forName,
}: {
  userId: string;
  /** l'admin règle l'avatar d'un autre compte (un « speaker ») : son prénom */
  forName?: string;
}) {
  const mine = useMySpeakerSprites(userId);
  const save = useSaveMySpeakerSprites(userId);
  const [slots, setSlots] = useState<SpriteSlot[]>(EMPTY);
  const [issue, setIssue] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [talking, setTalking] = useState(false);
  const [mouth, setMouth] = useState(0);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  // chemins enregistrés → emplacements (une fois chargés)
  useEffect(() => {
    if (!mine.data) return;
    setSlots(mine.data.map((path) => ({ path, file: null })));
  }, [mine.data]);

  // URL de chaque emplacement (nouvelle image : aperçu local)
  const urls = useMemo(
    () =>
      slots.map((s) => (s.file ? URL.createObjectURL(s.file) : s.path ? spriteUrl(s.path) : null)),
    [slots],
  );
  useEffect(
    () => () => {
      urls.forEach((u) => u?.startsWith('blob:') && URL.revokeObjectURL(u));
    },
    [urls],
  );

  const complete = urls.every((u): u is string => !!u);
  const dirty = slots.some((s) => s.file);

  // aperçu : la bouche bouge comme pendant une phrase
  useEffect(() => {
    if (!talking || !complete) {
      setMouth(0);
      return;
    }
    const started = Date.now();
    const id = setInterval(() => {
      const t = (Date.now() - started) % 2400;
      setMouth(mouthFrameAt([{ startMs: 0, endMs: 1500 }], t, true));
    }, MOUTH_FRAME_MS);
    return () => clearInterval(id);
  }, [talking, complete]);

  const pick = (i: number, file: File | undefined) => {
    setSaved(false);
    if (!file) return;
    const why = spriteFileIssue(file);
    if (why) {
      setIssue(`Image ${i + 1} : ${why}`);
      return;
    }
    setIssue(null);
    setSlots((prev) => prev.map((s, k) => (k === i ? { ...s, file } : s)));
  };

  const submit = () => {
    setIssue(null);
    save.mutate(slots, {
      onSuccess: () => setSaved(true),
      onError: (e) => setIssue(e instanceof Error ? e.message : 'Enregistrement impossible.'),
    });
  };

  return (
    <div className="space-y-4">
      <p className="text-ink-600 text-sm">
        {forName
          ? `L’avatar de ${forName} apparaît en haut à droite d’une animation quand c’est sa voix enregistrée qui joue (jamais avec la voix de synthèse), et au milieu du générique avec son prénom.`
          : 'Ton avatar apparaît en haut à droite d’une animation quand c’est ta voix enregistrée qui joue (jamais avec la voix de synthèse), et au milieu du générique avec ton prénom.'}
      </p>

      <div className="flex items-center gap-4">
        {complete ? (
          <SpeakerAvatar frames={urls as string[]} frame={mouth} size={88} />
        ) : (
          <div className="bg-ink-100 text-ink-500 flex h-[88px] w-[88px] items-center justify-center rounded-full text-center text-xs">
            8 images
          </div>
        )}
        <Button
          type="button"
          variant="outline"
          disabled={!complete}
          onClick={() => setTalking((t) => !t)}
          aria-pressed={talking}
        >
          {talking ? 'Arrêter l’aperçu' : 'Le faire parler'}
        </Button>
      </div>

      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {slots.map((s, i) => (
          <li key={i}>
            <button
              type="button"
              onClick={() => inputs.current[i]?.click()}
              className="border-ink-200 flex w-full flex-col items-center gap-1 rounded-xl border p-2 text-center active:scale-[0.98]"
              aria-label={`Remplacer l’image ${spriteSlotLabel(i)}`}
            >
              {urls[i] ? (
                <img
                  src={urls[i]!}
                  alt=""
                  className="h-20 w-full rounded-lg object-contain"
                  draggable={false}
                />
              ) : (
                <span className="bg-ink-100 text-ink-400 flex h-20 w-full items-center justify-center rounded-lg text-xs">
                  Ajouter
                </span>
              )}
              <span className="text-ink-600 text-xs">
                {spriteSlotLabel(i)}
                {s.file ? ' · nouvelle' : ''}
              </span>
            </button>
            <input
              ref={(el) => {
                inputs.current[i] = el;
              }}
              type="file"
              accept={SPRITE_MIME_TYPES.join(',')}
              className="hidden"
              data-testid={`sprite-input-${i}`}
              onChange={(e) => {
                pick(i, e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </li>
        ))}
      </ul>

      {issue && (
        <Alert variant="error">
          <AlertDescription>{issue}</AlertDescription>
        </Alert>
      )}
      {saved && !dirty && <p className="text-sm text-green-700">Avatar enregistré.</p>}

      <Button
        type="button"
        className="w-full sm:w-auto"
        disabled={!complete || !dirty || save.isPending}
        onClick={submit}
      >
        {save.isPending ? 'Enregistrement…' : 'Enregistrer'}
      </Button>
    </div>
  );
}
